import { Component, ChangeDetectionStrategy, Inject, Input } from '@angular/core';
import { Observable } from 'rxjs';
import { map, publishReplay, refCount } from 'rxjs/operators'; 
import { GridOptions } from 'ag-grid-community';
import { chain, sortBy, some, uniq, uniqWith, isEmpty, isNil } from 'lodash';
import { FilterFieldValue, FilterGridColDef } from '../../covance-filter';
import { CovanceFilter } from '../../covance-filter/covance-filter';
import { comparators } from '../../core';
import { MicrobiologyData } from './microbiology-data';
import { CultureRow, GrowthDetails, TextDetails } from './culture-details-ag-renderer.component';
import { SubjectTitleRenderer } from '../../renderer/subject-title-renderer';
import { DataRevisionIndicator } from '../../shared';
import { MicrobiologyGridColumns } from './microbiology-grid-columns';
import { formatters } from './../../core';
import { CultureGroupAgRendererComponent } from './culture-group-ag-renderer.component';
import { TermsStorage } from '../../corporate/terms-storage';
import { GridLegendItem } from '../../legend';
import { createLegend } from '../osr-legend-generator';
import { covanceExcelStyles } from '../../covance-ag-grid/excel-styles';
import {MicrobiologyColumns} from './microbiology-columns';
import { MICROBIOLOGY_DATA_STORAGE, MicrobiologyDataStorage } from './microbiology-data-storage';
import { ActivatedRoute } from '@angular/router';
import { routeData } from '../../shared/route-helpers';

type MicrobiologySortArgs = (keyof MicrobiologyData)[];

export interface MicrobiologyColDef extends FilterGridColDef {
    hasDataRevision?: (row: CultureRow) => boolean;
    getDataRevisionMessage?: (dataRevisionIndicator: DataRevisionIndicator) => string;
    isCancelled?: (row: CultureRow) => boolean;
}

const interpretationDescriptions: { [symbol: string]: string } = {
    S: 'Susceptible',
    I: 'Intermediate, Susceptible, Increased Exposure',
    R: 'Resistant',
    NA: 'Not applicable',
    NS: 'Non Susceptible',
    SDD: 'Susceptible Dose Dependent'
};

@Component({
    selector: 'microbiology',
    template: require('./microbiology.component.html'),
    styles: [require('./microbiology.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MicrobiologyComponent {

    @Input()
    public siteId: string;

    public columns: MicrobiologyColDef[];

    public exportColumns: FilterGridColDef[];

    public gridOptions: GridOptions;

    public fieldVals: FilterFieldValue[] = [];
    public results: Observable<CultureRow[]>;
    public exportResults: MicrobiologyData[];
    public exportGrid: GridOptions = {
        excelStyles: covanceExcelStyles
    };

    public get disclaimer() {
        return this.termsStorage.osrDisclaimerText;
    }

    public legend: Observable<GridLegendItem[]>;

    constructor(
        route: ActivatedRoute,
        private filter: CovanceFilter,
        @Inject(MICROBIOLOGY_DATA_STORAGE) private microbiologyStorage: MicrobiologyDataStorage,
        private microbiologyColumns: MicrobiologyColumns,
        microbiologyGridColumns: MicrobiologyGridColumns,
        private termsStorage: TermsStorage) {

        this.columns = this.microbiologyColumns.columns;
        this.exportColumns = this.microbiologyColumns.exportColumns;
        this.gridOptions = {
            groupRowRendererParams: { suppressCount: true },
            groupDefaultExpanded: this.columns.filter(col => !isNil(col.rowGroupIndex)).length,
            groupUseEntireRow: true,
            groupRowInnerRendererFramework: CultureGroupAgRendererComponent,
            rowClass: 'ag-row-group-ellipsis'
        };

        const filterColumns = microbiologyGridColumns.columnsDict['isolate-culture-data'];

        const data = this.microbiologyStorage.siteMicrobiologyData;
        this.fieldVals = this.filter.getFieldValues(filterColumns);

        const filteredData = this.filter.getFilteredData(data, this.fieldVals)
            .pipe(
                publishReplay(1),
                refCount()
        );

        this.results = filteredData
            .pipe(map((rows: MicrobiologyData[]) => {
                if (!rows) {
                    return null;
                }

                const baseSortDataArgs: MicrobiologySortArgs = ['subjectNumber', 'initials', 'screeningNumber', 'age', 'sex', 'visitDescription', 'visitColumnKey', 'testGroupName'];
                rows = sortBy(rows, baseSortDataArgs);

                const sortExportDataArgs: MicrobiologySortArgs = [...baseSortDataArgs, 'specimen', 'growth', 'organism', 'testSequence', 'testDescription', 'result'];

                this.exportResults = sortBy(rows, sortExportDataArgs);

                const hierarchyRows = rows.map(row => {
                    return {
                        subject: this.getSubjectTitle(row) || '',
                        visit: this.getVisitTitle(row) || '',
                        testGroup: row.testGroupName || '',
                        specimen: row.specimen || '',
                        entity: row
                    } as HierarchyRow;
                });

                const uniqHierarchyRows = uniqWith(hierarchyRows, this.isEqualHierarchyRows);

                const results: CultureRow[] = [];

                uniqHierarchyRows.forEach((uniqHierarchyRow: HierarchyRow) => {
                    const groupResults: CultureRow[] = [];
                    const values = hierarchyRows.filter(row => this.isEqualHierarchyRows(uniqHierarchyRow, row));

                    const groupsByGrowth = this.groupHierarchyRows(values, d => d.entity.growth);
                    let order = 1;

                    groupsByGrowth.forEach((groupByGrowth: GroupItem) => {
                        const groupsByOrganism = this.groupHierarchyRows(groupByGrowth.values, d => d.entity.fullOrganismTitle);
                        groupsByOrganism.forEach((groupByOrganism: GroupItem) => {
                            const agents = uniqWith(groupByOrganism.values, this.isEqualAgentRows)
                                .map((d: HierarchyRow) => d.entity)
                                .filter((d: MicrobiologyData) => !isEmpty(d.agent));

                            const growth = this.getGroupName(groupByGrowth.key);
                            const organism = this.getGroupName(groupByOrganism.key);

                            if (!isEmpty(growth) || !isEmpty(organism) || !isEmpty(agents)) {
                                const cultureRow = this.createCultureRow(uniqHierarchyRow);
                                cultureRow.growthDetails = {
                                    growth: growth,
                                    organism: organism,
                                    hasGrowthResultDataRevision: some(groupByGrowth.values, e => e.entity.hasGrowthResultDataRevision),
                                    hasOrganismResultDataRevision: some(groupByOrganism.values, e => e.entity.hasOrganismResultDataRevision),
                                    order: order++,
                                    agents: agents
                                } as GrowthDetails;

                                groupResults.push(cultureRow);
                            }
                        });
                    });

                    const cancellationReasonResults: CultureRow[] = [];
                    let subTestResults: CultureRow[] = [];

                    const cancellationReasons = values.map(d => d.entity.cancellationReason)
                        .filter(d => !isEmpty(d));

                    uniq(cancellationReasons).forEach((reason: string) => {
                        const cultureRow = this.createCultureRow(uniqHierarchyRow);
                        cultureRow.textDetails = {
                            title: 'Cancellation Reason',
                            value: reason,
                            isCancelled: true
                        } as TextDetails;
                        cancellationReasonResults.push(cultureRow);
                    });

                    const tests = uniqWith(values, this.isEqualTestRows)
                        .map((d: HierarchyRow) => d.entity)
                        .filter((d: MicrobiologyData) => !isEmpty(d.testDescription) && d.testDescription.toLowerCase() !== 'growth' /*Exclude growth, it is not a subtest*/);

                    if (tests.length > 0) {
                        tests.forEach((d: MicrobiologyData) => {
                            const cultureRow = this.createCultureRow(uniqHierarchyRow);
                            cultureRow.cancellationReason = d.subCancellationReason;
                            cultureRow.isCancelled = d.isSubCancelled;
                            cultureRow.textDetails = {
                                title: d.testDescription,
                                value: d.result,
                                hasTestResultDataRevision: d.hasTestResultDataRevision,
                                isQuestion: d.isQuestion,
                                testCollectionDateTime: d.testCollectionDateTime,
                                testAccessionId: d.testAccessionId,
                                testSequence: d.testSequence,
                                sequence: d.sequence
                            } as TextDetails;

                            subTestResults.push(cultureRow);
                        });
                    }

                    subTestResults = subTestResults.sort((a: CultureRow, b: CultureRow) => this.cultureRowComparer(a, b));

                    groupResults.push(...cancellationReasonResults, ...subTestResults);
                    if (groupResults.length > 0) {
                        results.push(...groupResults);
                    } else {
                        results.push(this.createCultureRow(uniqHierarchyRow));
                    }
                });    
                
                return results;
            }));

        this.legend = filteredData.pipe(map(x => this.createLegend(x)));
    }
    
    public onGridQuickFilterChanged(filter: string) {
        if (this.exportGrid) {
            this.exportGrid.quickFilterText = filter;
        }
    }
    
    private cultureRowComparer(a: CultureRow, b: CultureRow) {
        let result = comparators.numberComparator(a.textDetails.testSequence, b.textDetails.testSequence);
        if (result === 0) {
            result = comparators.stringComparator(a.textDetails.title, b.textDetails.title);

            if (result === 0) {
                result = comparators.numberComparator(a.textDetails.sequence, b.textDetails.sequence);
            }
        }
        return result;
    }

    private createLegend(microData: MicrobiologyData[]) {
        const legend = createLegend(microData);
        const interpretationlegend = chain(microData)
            .map(r => [r.micPanelInterpretation, r.kirbyBauerInterpretation])
            .flatten()
            .uniq()
            .filter(d => d)
            .sort()
            .map((l: string) => ({
                text: l,
                cssClass: `interpretation-badge ${l}`,
                label: interpretationDescriptions[l] || ''
            }))
            .value();
        legend.push(...interpretationlegend);
        return legend;
    }

    private getSubjectTitle(row: MicrobiologyData) {
        let title = SubjectTitleRenderer.renderTitle({
            data: {
                subjectNumber: row.subjectNumber,
                initials: row.initials,
                screeningNumber: row.screeningNumber,
                zcSubjectId: row.zcSubjectId
            }
        });

        if (!isEmpty(row.screeningNumber)) {
            title += ` (${row.screeningNumber})`;
        }

        if (!isNil(row.age) && !row.isAgeProject) {
            title += ` ${row.age} ${row.ageUnit}`;
        }

        if (!isNil(row.ageProjectDisplayValue) && row.isAgeProject) {
            title += ` ${row.ageProjectDisplayValue}`;
        }

        if (!isEmpty(row.sex)) {
            title += ` ${row.sex}`;
        }

        return title;
    }

    private getGroupName(groupName: string) {
        return (!isNil(groupName) && groupName !== 'null') ? groupName : '';
    }

    private getVisitTitle(row: MicrobiologyData) {
        const titleParts = [
            row.visitDescription,
            formatters.formatDateTime(row.collectionDateTime),
            row.accessionNumber
        ];

        return titleParts
            .filter(d => !isEmpty(d))
            .join(', ');
    }

    private isEqualHierarchyRows(a: HierarchyRow, b: HierarchyRow) {
        return a.subject === b.subject &&
            a.visit === b.visit &&
            a.testGroup === b.testGroup &&
            a.specimen === b.specimen;
    }

    private isEqualAgentRows(a: HierarchyRow, b: HierarchyRow) {
        return a.entity.agent === b.entity.agent &&
            a.entity.micResult === b.entity.micResult &&
            a.entity.micPanelInterpretation === b.entity.micPanelInterpretation &&
            a.entity.kirbyBauerPanelZone === b.entity.kirbyBauerPanelZone &&
            a.entity.kirbyBauerInterpretation === b.entity.kirbyBauerInterpretation;
    }

    private isEqualTestRows(a: HierarchyRow, b: HierarchyRow) {
        return a.entity.testDescription === b.entity.testDescription &&
            a.entity.result === b.entity.result;
    }

    private groupHierarchyRows(rows: HierarchyRow[], groupByFn: (row: HierarchyRow) => string): GroupItem[] {
        return chain(rows)
            .groupBy(groupByFn)
            .map((values: HierarchyRow[], key: string) => {
                return {
                    key: key,
                    values: values
                } as GroupItem;
            })
            .sortBy((d: GroupItem) => d.key)
            .value();
    }

    private createCultureRow(row: HierarchyRow) {
        return {
            subject: row.subject,
            visitDescription: row.visit,
            testGroupName: row.testGroup,
            specimen: row.specimen,
            hasSubjectDataRevision: row.entity.hasSubjectDataRevision,
            hasVisitDataRevision: row.entity.hasVisitDataRevision,
            hasSpecimenDataRevision: row.entity.hasSpecimenResultDataRevision,
            dataChangeDetails: {
                microTestId: row.entity.microTestId,
                subjectNumber: row.entity.subjectNumber,
                accessionNumber: row.entity.accessionNumber,
                visitDescription: row.entity.visitDescription
            }
        } as CultureRow;
    }
}

interface GroupItem {
    key: string;
    values: HierarchyRow[];
}

interface HierarchyRow {
    subject: string;
    visit: string;
    testGroup: string;
    specimen: string;
    entity: MicrobiologyData;
}
